import { type ChangeEvent, type FormEvent, useCallback, useRef, useState } from 'react';

import { getUniqueSuffixedName, matchesSavedName } from '../utils/savedSizes';

export type CollidableItem = {
  id: string;
  name: string;
};

export type UseSaveWithNameCollisionParams<Item extends CollidableItem> = {
  items: Item[];
  onSave: (params: { existingId?: string; name: string }) => Promise<boolean>;
  /** Fires after a successful save so callers can surface feedback (e.g. a toast) without this hook knowing what kind of item it saved. */
  onSaveSuccess: (params: { existingId?: string }) => void;
};

export type UseSaveWithNameCollisionResult<Item extends CollidableItem> = {
  collidingItem: Item | undefined;
  error: string | null;
  handleNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleOpenChange: (nextOpen: boolean) => void;
  handleSaveAsNew: () => void;
  handleSubmit: (event: FormEvent) => void;
  isSaving: boolean;
  name: string;
  open: boolean;
  trimmedName: string;
};

/**
 * Shared open/name/submit state for saved-size and saved-set popovers: both offer the same live
 * name-collision choice and must avoid retrying a name the backend already rejected.
 */
export const useSaveWithNameCollision = <Item extends CollidableItem>({
  items,
  onSave,
  onSaveSuccess,
}: UseSaveWithNameCollisionParams<Item>): UseSaveWithNameCollisionResult<Item> => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveSessionRef = useRef(0);

  const trimmedName = name.trim();
  const collidingItem = trimmedName ? items.find((item) => matchesSavedName(item.name, trimmedName)) : undefined;

  const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setName(event.target.value);
  }, []);

  const handleOpenChange = useCallback((nextOpen: boolean): void => {
    saveSessionRef.current += 1;
    setOpen(nextOpen);
    setName('');
    setError(null);
  }, []);

  const save = useCallback(
    // `nameOverride` lets "Save as new" submit the suffixed name in the same click that computes
    // it -- `name` state wouldn't have flushed yet if it relied on the just-called `setName`.
    async (existingId?: string, nameOverride?: string): Promise<void> => {
      const nameToSave = (nameOverride ?? name).trim();

      if (!nameToSave || isSaving) {
        return;
      }

      setIsSaving(true);
      setError(null);

      const sessionId = saveSessionRef.current;

      try {
        const succeeded = await onSave({ existingId, name: nameToSave });

        // The popover can be dismissed and reopened mid-save; a stale result must not close or clear the new session.
        const isSameSession = saveSessionRef.current === sessionId;

        if (succeeded) {
          if (isSameSession) {
            setOpen(false);
            setName('');
          }
          onSaveSuccess({ existingId });
        } else if (isSameSession) {
          setError('Could not save. Try again.');
        }
      } finally {
        setIsSaving(false);
      }
    },
    [isSaving, name, onSave, onSaveSuccess],
  );

  const handleSubmit = useCallback(
    (event: FormEvent): void => {
      event.preventDefault();
      void save(collidingItem?.id);
    },
    [collidingItem, save],
  );

  const handleSaveAsNew = useCallback((): void => {
    if (collidingItem) {
      const suffixedName = getUniqueSuffixedName(
        trimmedName,
        items.map((item) => item.name),
      );

      setName(suffixedName);
      void save(undefined, suffixedName);
      return;
    }

    void save();
  }, [collidingItem, items, save, trimmedName]);

  return {
    collidingItem,
    error,
    handleNameChange,
    handleOpenChange,
    handleSaveAsNew,
    handleSubmit,
    isSaving,
    name,
    open,
    trimmedName,
  };
};
